import React, { useCallback, useEffect, useState } from 'react';
import { MessageSquare, RefreshCw, PhoneOff, PhoneCall, CheckCircle2, AlertTriangle, Loader2, Download, BellRing } from 'lucide-react';
import api from '@/lib/api';

/**
 * AdminSMSStats — compact admin tile summarising the Twilio SMS pipeline.
 *
 * Pulls from GET /api/admin/sms/stats and surfaces:
 *   • Sent · Delivered · Failed · Skipped (all-time rolled up)
 *   • 24h + 7d rolling sent momentum
 *   • Opted-in vs stopped user counts (consent health)
 *   • Verified phones
 *   • Last 10 messages (category · last-4 · status · time)
 *
 * Sits next to AdminEmailEngagement so admins can compare email + SMS
 * program health without leaving the account dashboard.
 */

function fmtWhen(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch (_) { return iso; }
}

const STATUS_COLOR = {
  delivered: 'text-[#72C2AC]',
  'sent-test-mode': 'text-[#72C2AC]',
  queued: 'text-[#C4A67A]',
  sent: 'text-[#C4A67A]',
  failed: 'text-[#D96C6C]',
  undelivered: 'text-[#D96C6C]',
  'skipped-unconfigured': 'text-[#8A9A92]',
  unknown: 'text-[#8A9A92]',
};

export default function AdminSMSStats() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const { data: d } = await api.get('/admin/sms/stats');
      setData(d);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not load SMS stats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // HF-056 — CSV export of raw SMS failure rows from the last 24h.
  const downloadErrorsCsv = async () => {
    try {
      const resp = await api.get('/admin/sms/stats/errors.csv', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `sms_errors_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '')}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not export errors CSV');
    }
  };

  return (
    <div className="glass p-6 border border-[#72C2AC]/30" data-testid="admin-sms-stats">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MessageSquare size={14} className="text-[#72C2AC]" />
          <div className="label-tiny text-[#72C2AC]">Admin · SMS Pipeline</div>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="admin-sms-stats-csv"
            onClick={downloadErrorsCsv}
            className="text-[11px] px-2 py-1 rounded-full bg-[#C4A67A]/15 border border-[#C4A67A]/35 text-[#C4A67A] hover:bg-[#C4A67A]/25 transition-colors inline-flex items-center gap-1"
            title="Export the last 24h of SMS failures as CSV"
          >
            <Download size={12} /> Errors CSV
          </button>
          <button
            data-testid="admin-sms-stats-refresh"
            onClick={load}
            disabled={loading}
            className="text-[11px] text-[#72C2AC] hover:text-[#C4A67A] inline-flex items-center gap-1 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {err && (
        <div className="text-xs text-[#D96C6C] mb-3 inline-flex items-center gap-1">
          <AlertTriangle size={11} /> {err}
        </div>
      )}

      {!data ? (
        <div className="text-xs text-[#8A9A92] inline-flex items-center gap-1">
          <Loader2 size={11} className="animate-spin" /> Loading…
        </div>
      ) : (
        <>
          {/* Headline metric row — mirrors AdminEmailEngagement layout. */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="admin-sms-stats-summary">
            <SummaryStat label="Sent" value={data.sent} testid="admin-sms-stats-sent" />
            <SummaryStat label="Delivered" value={data.delivered} testid="admin-sms-stats-delivered" />
            <SummaryStat label="Failed" value={data.failed} tone="danger" testid="admin-sms-stats-failed" />
            <SummaryStat label="Opted-in" value={data.opted_in} testid="admin-sms-stats-optedin" />
          </div>

          {/* Momentum + consent row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <MiniStat icon={<MessageSquare size={11} />} label="Last 24h" value={data.sent_24h} testid="admin-sms-stats-24h" />
            <MiniStat icon={<MessageSquare size={11} />} label="Last 7d" value={data.sent_7d} testid="admin-sms-stats-7d" />
            <MiniStat icon={<CheckCircle2 size={11} className="text-[#72C2AC]" />} label="Verified" value={data.verified_users} testid="admin-sms-stats-verified" />
            <MiniStat icon={<PhoneOff size={11} className="text-[#D96C6C]" />} label="Stopped" value={data.stopped} testid="admin-sms-stats-stopped" />
          </div>

          {/* Category breakdown */}
          {Object.keys(data.by_category || {}).length > 0 && (
            <div className="mb-5">
              <div className="label-tiny text-[#8A9A92] mb-2">Sends by category</div>
              <div className="glass-soft p-3 flex flex-wrap gap-x-4 gap-y-1.5 text-[12px]">
                {Object.entries(data.by_category).map(([cat, count]) => (
                  <div key={cat} className="flex items-center gap-1.5" data-testid={`admin-sms-stats-cat-${cat}`}>
                    <span className="font-mono text-[#C4A67A]">{cat}</span>
                    <span className="text-[#5A6B65]">·</span>
                    <span className="text-[#E8E3D9]">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* HF-056 — Twilio error-code breakdown (last 24h). Compact
              glass-soft list so you can see which codes dominate without
              leaving the dashboard. "No errors" skeleton keeps the shape
              consistent when things are healthy. */}
          <div className="mb-5" data-testid="admin-sms-stats-errors">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle size={11} className="text-[#D96C6C]" />
              <div className="label-tiny text-[#8A9A92]">Twilio errors · last 24h</div>
            </div>
            {(!data.error_breakdown_24h || data.error_breakdown_24h.length === 0) ? (
              <div
                className="glass-soft p-3 text-[12px] text-[#8A9A92]"
                data-testid="admin-sms-stats-errors-empty"
              >
                No Twilio errors returned in the last 24h.
              </div>
            ) : (
              <div className="space-y-1.5">
                {data.error_breakdown_24h.map((e) => (
                  <div
                    key={String(e.error_code)}
                    className="glass-soft p-2.5 flex items-center justify-between gap-3 text-[12px]"
                    data-testid={`admin-sms-stats-error-${e.error_code}`}
                  >
                    <div className="min-w-0 flex-1 flex items-center gap-2">
                      <span className="font-mono text-[#D96C6C] flex-shrink-0">
                        {e.error_code === 'no_code' ? '—' : e.error_code}
                      </span>
                      <span className="text-[#5A6B65]">·</span>
                      <span className="text-[#E8E3D9] truncate">{e.label}</span>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-[#C4A67A] font-mono">×{e.count}</div>
                      {e.last_at && (
                        <div className="text-[10px] text-[#5A6B65]">{fmtWhen(e.last_at)}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* HF-056 — Re-engagement nudges per tier (24h + 7d). Four fixed
              columns so operators can read 72h → 30d churn-flow at a
              glance even when a tier is zero. */}
          <div className="mb-5" data-testid="admin-sms-stats-nudges">
            <div className="flex items-center gap-2 mb-2">
              <BellRing size={11} className="text-[#C4A67A]" />
              <div className="label-tiny text-[#8A9A92]">Re-engagement nudges</div>
            </div>
            <div className="glass-soft p-3">
              <div className="grid grid-cols-4 gap-3 text-center">
                {['72h', '7d', '14d', '30d'].map((tier) => (
                  <div key={tier} data-testid={`admin-sms-stats-nudge-${tier}`}>
                    <div className="text-[10px] uppercase tracking-widest text-[#5A6B65]">{tier}</div>
                    <div className="font-display text-[22px] text-[#C4A67A] leading-none mt-1">
                      {data.nudges_by_tier?.last_24h?.[tier] ?? 0}
                    </div>
                    <div className="text-[9px] text-[#8A9A92] mt-0.5">last 24h</div>
                    <div className="text-[10px] text-[#72C2AC] mt-1 font-mono">
                      7d · {data.nudges_by_tier?.last_7d?.[tier] ?? 0}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recent sends strip */}
          <div>
            <div className="label-tiny text-[#8A9A92] mb-2">Recent messages</div>
            {(!data.recent || data.recent.length === 0) ? (
              <div className="text-xs text-[#8A9A92]" data-testid="admin-sms-stats-empty">
                No SMS messages yet.
              </div>
            ) : (
              <div className="space-y-1.5 max-h-[240px] overflow-y-auto custom-scrollbar pr-1">
                {data.recent.map((m) => {
                  const statusCls = STATUS_COLOR[m.status] || 'text-[#8A9A92]';
                  return (
                    <div
                      key={m.id}
                      className="glass-soft p-2.5 flex items-center justify-between text-[12px] gap-3"
                      data-testid={`admin-sms-stats-row-${m.id}`}
                    >
                      <div className="min-w-0 flex-1 flex items-center gap-2">
                        <PhoneCall size={11} className="text-[#8A9A92] flex-shrink-0" />
                        <span className="font-mono text-[#C4A67A]">····{m.phone_last4 || '????'}</span>
                        <span className="text-[#5A6B65]">·</span>
                        <span className="text-[#E8E3D9] truncate">{m.category || 'unknown'}</span>
                      </div>
                      <div className="text-right text-[10px] font-mono flex-shrink-0">
                        <div className="text-[#5A6B65]">{fmtWhen(m.sent_at)}</div>
                        <div className={`${statusCls} mt-0.5`}>{m.status}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function SummaryStat({ label, value, suffix, tone, testid }) {
  const valColor = tone === 'danger' && (value ?? 0) > 0 ? 'text-[#D96C6C]' : 'text-[#E8E3D9]';
  return (
    <div className="glass-soft p-3 text-center" data-testid={testid}>
      <div className="text-[11px] text-[#8A9A92] uppercase tracking-widest">{label}</div>
      <div className={`text-[22px] font-display ${valColor} leading-tight mt-1`}>{value ?? 0}</div>
      {suffix && <div className="text-[10px] text-[#C4A67A] font-mono">{suffix}</div>}
    </div>
  );
}

function MiniStat({ icon, label, value, testid }) {
  return (
    <div className="glass-soft p-2.5 flex items-center justify-between" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] text-[#8A9A92] uppercase tracking-wider">
        {icon} {label}
      </div>
      <div className="text-[14px] font-display text-[#E8E3D9]">{value ?? 0}</div>
    </div>
  );
}
